import { useEffect, useMemo, useState } from "react";
import { EyeIcon, TrashIcon } from "@heroicons/react/outline";
import { useDeletePatientMutation } from "../../../redux/reduxQuery/patientApi";
import { useNavigate } from "react-router-dom";

import { TitleM } from "../../../components/atoms/Typography";
import { EyeTrashIconButtons } from "../../../components/molecules/TableActionButtons";
import { ContentWrapper } from "../../../components/ContentWrapper";
import { ModalWithTwoButtons } from "../../../components/molecules/Modal";
import { CoreTable } from "../../../components/organisms/CoreTable";
import { formatDate } from "../../../utils/formatDate";
import { RenderCell } from "../../../common/fv";
import { useMutationWrapper } from "../../../common/api";
import { formatSSN } from "../../../common/format";
import { useGetData } from "./table/data";
import { useAppSelector } from "../../../hooks/redux";
import { filtersProvider } from "../../../utils/filterProvider";

export default function Patient() {
  const [isOpen, setIsOpen] = useState(false);
  const [patient, setPatient] = useState<{ id: String; name: string }>();
  const navigate = useNavigate();
  const [deletePatientMutation] = useDeletePatientMutation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const globalFilter = useAppSelector((s) => s.filter?.patients);
  const storedPageSize = useAppSelector(
    (s) => s.filter?.patientsControl?.pageSize
  );
  const [filterValues, setFilterValues] = useState(globalFilter || {});

  const { config, refetch, dataToFetch, setDataToFetch } = useGetData();

  useEffect(() => {
    const qs = filtersProvider(filterValues);
    if (qs !== dataToFetch) {
      setDataToFetch(qs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        filterName: "Name",
        canFilter: true,
        includeToolTip: true,
      },
      {
        Header: "Birth Date",
        accessor: "dateOfBirth",
        filterName: "Birth Date",
        canFilter: true,
        Cell: ({ row }) => formatDate(row.original.dateOfBirth),
      },
      {
        Header: "SSN",
        accessor: "ssn",
        filterName: "SSN",
        canFilter: true,
        Cell: ({ row }) => formatSSN(row.original.ssn),
      },
      {
        Header: "Law Firm",
        accessor: "firm.name",
        filterName: "Law Firm",
        canFilter: true,
        includeToolTip: true,
      },
      {
        Header: "FV Status",
        accessor: "fileVineID",
        Cell: (cellProps) => RenderCell(cellProps, 0),
      },
      {
        Header: "Actions",
        accessor: "id",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => {
          const patientId = row.original.id;
          return (
            <EyeTrashIconButtons
              link={`/admin/patients/${patientId}`}
              icons={[EyeIcon, TrashIcon]}
              onClick1={() => navigate(`/admin/patients/${patientId}`)}
              onClick2={() => {
                setPatient({ id: row.original.id, name: row.original.name });
                setIsOpen(true);
              }}
            />
          );
        },
      },
    ],
    [navigate]
  );

  const filterButton = {
    label: "Filters",
    icon: "AdjustmentsVerticalIcon",
    base: "secondary",
    onClick: () => setOpenFilters(true),
    btnContainerClasses: "box-border border-2 border-primary-400 border-solid",
  };

  const mutate = useMutationWrapper(
    deletePatientMutation,
    `Patient Deleted Successfully`,
    () => {
      setIsProcessing(false);
      refetch();
      setIsOpen(false);
    }
  );

  const deletePatient = () => {
    setIsProcessing(true);
    mutate(patient?.id);
  };

  return (
    <>
      {isOpen && (
        <ModalWithTwoButtons
          title="delete patient"
          content={`Are you sure you want to delete ${patient?.name}? If you do you won't be able to recover it.`}
          button1Text="delete"
          button2Text="cancel"
          loader={isProcessing}
          onClick1={() => deletePatient()}
          onClick2={() => setIsOpen(false)}
        />
      )}
      <ContentWrapper>
        <TitleM style={{ margin: "1rem 0" }}>Patients</TitleM>
        <CoreTable
          tableType="patients"
          columns={columns}
          setFilterValues={setFilterValues}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          actionButtonsConfig={[
            {
              label: "Add New",
              onClick: () => navigate("/admin/patients/new"),
              icon: "PlusIcon",
            },
          ]}
          actionRightButtons={[filterButton]}
          pageSize={Number(storedPageSize ?? 50)}
          {...config}
        />
      </ContentWrapper>
    </>
  );
}
